import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import { mainSiteUrl } from '@/mocks/categories';
import JsonLd, { siteUrl, siteName, logoUrl } from '@/components/feature/JsonLd';

export default function ArticlePage() {
  const { t, i18n } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  useEffect(() => {
    document.title = t('metaTitle');
  }, [t]);

  const whyList = t('whyList', { returnObjects: true }) as string[];
  const safetyList = t('safetyList', { returnObjects: true }) as string[];

  const compareRows = [
    [t('compareOrg'), t('compareHigh'), t('compareMedium'), t('compareHigh')],
    [t('compareFilter'), t('compareHigh'), t('compareMedium'), t('compareHigh')],
    [t('compareBeginner'), t('compareHigh'), t('compareHigh'), t('compareMedium')],
    [t('compareMaint'), t('compareMedium'), t('compareMedium'), t('compareHigh')],
    [t('compareBest'), t('compareQuick'), t('compareBrowse'), t('compareLarge')],
  ];

  const faqItems = [
    { q: t('faqQ1'), a: t('faqA1') },
    { q: t('faqQ2'), a: t('faqA2') },
    { q: t('faqQ3'), a: t('faqA3') },
    { q: t('faqQ4'), a: t('faqA4') },
  ];

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: t('h1'),
        description: t('metaDescription'),
        inLanguage: i18n.language,
        dateModified: '2026-08-23',
        url: `${siteUrl}/usfans-spreadsheet-guide`,
        publisher: {
          '@type': 'Organization',
          name: siteName,
          logo: { '@type': 'ImageObject', url: logoUrl },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${siteUrl}/usfans-spreadsheet-guide`,
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
          { '@type': 'ListItem', position: 2, name: t('h1'), item: `${siteUrl}/usfans-spreadsheet-guide` },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-[#fbfaf8]">
      <JsonLd data={articleJsonLd} />
      <SiteHeader />
      <main>
        <article className="mx-auto max-w-3xl px-4 py-10 md:px-6 md:py-14">
          {/* Breadcrumb / internal link back home */}
          <nav className="flex items-center gap-2 text-xs text-stone-500">
            <Link to="/" className="flex items-center gap-1 hover:text-stone-800">
              <i className="ri-home-4-line w-4 h-4 flex items-center justify-center" aria-hidden="true" />
              {t('breadcrumbHome')}
            </Link>
            <span className="text-stone-300">/</span>
            <span className="text-stone-700">USFans Spreadsheet</span>
          </nav>

          <header className="mt-6">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                {t('badge')}
              </span>
              <span className="text-xs text-stone-400">{t('updated')}</span>
            </div>
            <h1 className="mt-4 text-2xl font-bold leading-tight text-stone-900 md:text-3xl lg:text-4xl">
              {t('h1')}
            </h1>
          </header>

          <div className="mt-6 space-y-5 text-base leading-relaxed text-stone-700">
            <p>{t('introP1')}</p>
            <p>{t('introP2')}</p>
          </div>

          <section className="mt-10">
            <h2 className="text-xl font-bold text-stone-900 md:text-2xl">{t('whatTitle')}</h2>
            <div className="mt-4 space-y-4 text-base leading-relaxed text-stone-700">
              <p>{t('whatP1')}</p>
              <p>{t('whatP2')}</p>
            </div>
          </section>

          <section className="mt-10">
            <h2 className="text-xl font-bold text-stone-900 md:text-2xl">{t('whyTitle')}</h2>
            <p className="mt-3 text-base leading-relaxed text-stone-700">{t('whyLead')}</p>
            <ul className="mt-4 space-y-2.5">
              {whyList.map((item) => (
                <li key={item} className="flex items-start gap-3 text-base text-stone-700">
                  <span className="mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                    <i className="ri-check-line w-3.5 h-3.5 flex items-center justify-center" aria-hidden="true" />
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Comparison table */}
          <section className="mt-10">
            <h2 className="text-xl font-bold text-stone-900 md:text-2xl">{t('compareTitle')}</h2>
            <p className="mt-3 text-base leading-relaxed text-stone-700">{t('compareIntro')}</p>
            <div className="mt-4 overflow-x-auto rounded-lg border border-stone-200 bg-white">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-stone-200 bg-stone-50 text-stone-700">
                    <th className="px-4 py-3 font-semibold">{t('compareColFeature')}</th>
                    <th className="px-4 py-3 font-semibold">{t('compareColSpreadsheet')}</th>
                    <th className="px-4 py-3 font-semibold">{t('compareColDirectory')}</th>
                    <th className="px-4 py-3 font-semibold">{t('compareColDatabase')}</th>
                  </tr>
                </thead>
                <tbody>
                  {compareRows.map((row) => (
                    <tr key={row[0]} className="border-b border-stone-100 last:border-0">
                      <td className="px-4 py-3 font-medium text-stone-800">{row[0]}</td>
                      <td className="px-4 py-3 text-stone-600">{row[1]}</td>
                      <td className="px-4 py-3 text-stone-600">{row[2]}</td>
                      <td className="px-4 py-3 text-stone-600">{row[3]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="mt-10">
            <h2 className="text-xl font-bold text-stone-900 md:text-2xl">{t('safetyTitle')}</h2>
            <p className="mt-3 text-base leading-relaxed text-stone-700">{t('safetyP1')}</p>
            <ul className="mt-4 space-y-2.5">
              {safetyList.map((item) => (
                <li key={item} className="flex items-start gap-3 text-base text-stone-700">
                  <span className="mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                    <i className="ri-shield-check-line w-3.5 h-3.5 flex items-center justify-center" aria-hidden="true" />
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* FAQ */}
          <section className="mt-10">
            <h2 className="text-xl font-bold text-stone-900 md:text-2xl">{t('faqTitle')}</h2>
            <div className="mt-4 space-y-2">
              {faqItems.map((item, index) => {
                const isOpen = openFaq === index;
                return (
                  <div key={item.q} className="overflow-hidden rounded-lg border border-stone-200 bg-white">
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : index)}
                      className="flex w-full items-center justify-between px-4 py-3.5 text-left text-sm font-semibold text-stone-800 hover:bg-stone-50 whitespace-nowrap cursor-pointer"
                      aria-expanded={isOpen}
                    >
                      <span>{item.q}</span>
                      <i
                        className={`ri-arrow-down-s-line w-5 h-5 flex items-center justify-center text-stone-400 transition-transform ${
                          isOpen ? 'rotate-180' : ''
                        }`}
                        aria-hidden="true"
                      />
                    </button>
                    {isOpen && (
                      <div className="border-t border-stone-100 px-4 py-3 text-sm leading-relaxed text-stone-600">
                        {item.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* CTA + internal link */}
          <section className="mt-12 rounded-lg border border-stone-200 bg-white p-6 text-center md:p-8">
            <h2 className="text-lg font-bold text-stone-900 md:text-xl">{t('ctaTitle')}</h2>
            <p className="mt-2 text-sm text-stone-600">{t('ctaSubtitle')}</p>
            <div className="mt-5 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-emerald-600 px-6 py-3 text-sm font-semibold text-white hover:bg-emerald-700 whitespace-nowrap cursor-pointer"
              >
                {t('ctaButton')}
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center" aria-hidden="true" />
              </a>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-stone-200 px-6 py-3 text-sm font-semibold text-stone-700 hover:bg-stone-50 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" aria-hidden="true" />
                {t('backHome')}
              </Link>
            </div>
          </section>
        </article>
      </main>
      <SiteFooter />
    </div>
  );
}